// src/utils/seoManager.js

/**
 * Gestionnaire SEO centralisé pour gérer toutes les métadonnées
 */
class SEOManager {
  constructor() {
    this.baseUrl = process.env.VUE_APP_BASE_URL || 'https://cassuss.com'
    this.defaultImage = '/images/og-default.jpg'
    this.siteName = 'cassuss E-commerce'
    
    // Stockage des schemas déjà injectés
    this.injectedSchemas = new Set()
  }

  /**
   * Définit toutes les balises meta pour une page
   */
  setMetaTags(config) {
    // Titre de la page
    if (config.title) {
      document.title = config.title
    }

    // Meta tags de base
    this.updateMetaTag('description', config.description)
    this.updateMetaTag('keywords', config.keywords)
    this.updateMetaTag('robots', config.robots || 'index, follow')
    this.updateMetaTag('author', 'cassuss E-commerce')
    
    // Langue et région
    this.updateMetaTag('language', 'fr')
    this.updateMetaTag('geo.region', 'CM')
    this.updateMetaTag('geo.placename', 'Cameroun')

    // URL canonique
    if (config.url) {
      this.setCanonicalUrl(config.url)
    }

    // Open Graph
    this.updateMetaTag('og:title', config.title, 'property')
    this.updateMetaTag('og:description', config.description, 'property')
    this.updateMetaTag('og:image', config.image || this.defaultImage, 'property')
    this.updateMetaTag('og:url', config.url || window.location.href, 'property')
    this.updateMetaTag('og:type', config.ogType || 'website', 'property')
    this.updateMetaTag('og:locale', 'fr_FR', 'property')
    this.updateMetaTag('og:site_name', this.siteName, 'property')

    // Twitter Card
    this.updateMetaTag('twitter:card', 'summary_large_image')
    this.updateMetaTag('twitter:title', config.title)
    this.updateMetaTag('twitter:description', config.description)
    this.updateMetaTag('twitter:image', config.image || this.defaultImage)

    // Product meta (si produit)
    if (config.product) {
      this.updateMetaTag('product:price:amount', config.product.price, 'property')
      this.updateMetaTag('product:price:currency', 'XAF', 'property')
      this.updateMetaTag('product:availability', 
        config.product.availability || 'in stock', 'property')
    }

    console.log('✅ Meta tags mis à jour:', config.title)
  }

  /**
   * Met à jour ou crée une balise meta
   */
  updateMetaTag(name, content, attribute = 'name') {
    if (!content) return

    let element = document.querySelector(`meta[${attribute}="${name}"]`)
    
    if (!element) {
      element = document.createElement('meta')
      element.setAttribute(attribute, name)
      document.head.appendChild(element)
    }
    
    element.setAttribute('content', content)
  }

  /**
   * Définit l'URL canonique
   */
  setCanonicalUrl(url) {
    if (!url) return

    let link = document.querySelector('link[rel="canonical"]')
    
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'canonical')
      document.head.appendChild(link)
    }
    
    link.setAttribute('href', url)
  }

  /**
   * Injecte un schema JSON-LD
   */
  injectSchema(schema, id = null) {
    if (!schema) return

    // Créer un ID unique si non fourni
    const schemaId = id || `schema-${Date.now()}`
    
    // Supprimer l'ancien schema avec le même ID s'il existe
    if (id && this.injectedSchemas.has(id)) {
      this.removeSchema(id)
    }

    // Créer le script
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-schema-id', schemaId)
    script.textContent = JSON.stringify(schema, null, 2)
    document.head.appendChild(script)

    // Mémoriser l'ID
    this.injectedSchemas.add(schemaId)

    console.log('📊 Schema injecté:', schemaId, schema['@type'])
  }

  /**
   * Supprime un schema par son ID
   */
  removeSchema(id) {
    const script = document.querySelector(`script[data-schema-id="${id}"]`)
    if (script) {
      script.remove()
      this.injectedSchemas.delete(id)
    }
  }

  /**
   * Nettoie tous les schemas
   */
  clearSchemas() {
    const scripts = document.querySelectorAll('script[data-schema-id]')
    scripts.forEach(script => {
      const id = script.getAttribute('data-schema-id')
      this.injectedSchemas.delete(id)
      script.remove()
    })
    console.log('🧹 Tous les schemas nettoyés')
  }

  /**
   * Génère les données SEO pour un produit
   */
  generateProductSEO(product) {
    if (!product) return null

    const price = product.promo_price || product.price
    const brandName = product.brand?.name || 'cassuss'
    const imageUrl = this.getFullImageUrl(product.cover_image || product.image)

    return {
      title: `${product.name} | ${brandName} - ${this.siteName}`,
      description: this.truncateDescription(
        `${product.name} - ${product.description || ''} Prix: ${price} FCFA. ${product.in_stock ? 'En stock' : 'Rupture de stock'}. Livraison rapide au Cameroun.`,
        160
      ),
      keywords: [
        product.name,
        brandName,
        product.category?.name,
        'achat en ligne Cameroun',
        'e-commerce',
        'livraison Douala'
      ].filter(Boolean).join(', '),
      image: imageUrl,
      url: `${this.baseUrl}/product/${product.slug}`,
      ogType: 'product',
      robots: 'index, follow',
      product: {
        price: price,
        availability: product.in_stock ? 'in stock' : 'out of stock'
      }
    }
  }

  /**
   * Génère les données SEO pour une catégorie
   */
  generateCategorySEO(category, productsCount = 0) {
    if (!category) return null

    return {
      title: `${category.name} - Catalogue de produits | ${this.siteName}`,
      description: this.truncateDescription(
        `Découvrez notre collection ${category.name} avec ${productsCount} produits. Livraison rapide au Cameroun. Meilleurs prix garantis.`,
        160
      ),
      keywords: [
        category.name,
        'produits',
        'e-commerce Cameroun',
        'achats en ligne',
        'livraison Douala'
      ].filter(Boolean).join(', '),
      image: this.getFullImageUrl(category.image),
      url: `${this.baseUrl}/category/${category.slug}`,
      ogType: 'website',
      robots: 'index, follow'
    }
  }
    generateBrandSEO(brand, productsCount = 0) {
    if (!brand) return null

    return {
      title: `${brand.name} - Catalogue de produits  | ${this.siteName}`,
      description: this.truncateDescription(
        `Découvrez notre collection ${brand.name} avec ${productsCount} produits. Livraison rapide au Cameroun. Meilleurs prix garantis.`,
        160
      ),
      keywords: [
        brand.name,
        'produits',
        'e-commerce Cameroun',
        'achats en ligne',
        'livraison Douala'
      ].filter(Boolean).join(', '),
      image: this.getFullImageUrl(brand.image),
      url: `${this.baseUrl}/category/${brand.categorySlug}/${brand.slug}`,
      ogType: 'website',
      robots: 'index, follow'
    }
  }

  /**
   * Génère les données SEO pour la page d'accueil
   */
  generateHomeSEO() {
    return {
      title: `${this.siteName} - Votre boutique en ligne au Cameroun`,
      description: 'Découvrez notre large sélection de produits : mode, électronique, beauté et plus. Livraison rapide au Cameroun. Paiement sécurisé.',
      keywords: 'e-commerce Cameroun, boutique en ligne, achats en ligne Douala, livraison rapide, produits de qualité',
      image: this.defaultImage,
      url: this.baseUrl,
      ogType: 'website',
      robots: 'index, follow'
    }
  }

  /**
   * Obtient l'URL complète d'une image
   */
  getFullImageUrl(imagePath) {
    if (!imagePath) return `${this.baseUrl}${this.defaultImage}`
    if (imagePath.startsWith('http')) return imagePath
    
    const apiUrl = process.env.VUE_APP_API_URL || 'https://api.cassuss.com'
    return `${apiUrl}/product/product_images/${imagePath}`
  }

  /**
   * Tronque une description à une longueur maximale
   */
  truncateDescription(text, maxLength = 160) {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength - 3) + '...'
  }

  /**
   * Nettoie toutes les métadonnées (utile avant changement de page)
   */
  cleanup() {
    // On garde les meta tags de base et on met juste à jour leur contenu
    // Pas besoin de tout supprimer à chaque fois
  }
}

// Export d'une instance singleton
export default new SEOManager()
